import React from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { MainLayout } from './components/Layout/MainLayout';
import { Home } from './pages/Home';
import { Scanner } from './pages/Scanner';
import { Collection } from './pages/Collection';
import { Marketplace } from './pages/Marketplace';
import { Profile } from './pages/Profile';
import { Catalog } from './pages/Catalog';
import { CheckoutSuccess } from './pages/CheckoutSuccess';
import { BinderDetail } from './pages/BinderDetail';
import { WishlistDetail } from './pages/WishlistDetail';
import { RequireAuth } from './components/Auth/RequireAuth';
import { Login } from './pages/Login';
import { OAuthConsent } from './pages/OAuthConsent';
import { Terms } from './pages/Legal/Terms';
import { Privacy } from './pages/Legal/Privacy';
import { Refund } from './pages/Legal/Refund';
import { ToastContainer } from './components/Toast/Toast';

// Error Boundary for catching React render errors
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('App error boundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-obsidian flex items-center justify-center p-6">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-danger/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">!</span>
            </div>
            <h1 className="text-2xl font-display font-bold text-cream-50 mb-2">Something went wrong</h1>
            <p className="text-cream-200/60 mb-6 text-sm">
              An unexpected error occurred. Please try refreshing the page.
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.href = '/';
              }}
              className="px-6 py-3 bg-copper-500 hover:bg-copper-400 text-cream-50 rounded-xl font-semibold transition-colors"
            >
              Go Home
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// 404 Not Found page
const NotFound = () => (
  <div className="min-h-screen flex items-center justify-center p-6">
    <div className="text-center">
      <h1 className="text-6xl font-display font-bold text-copper-400 mb-2">404</h1>
      <h2 className="text-xl font-semibold text-cream-50 mb-2">Page Not Found</h2>
      <p className="text-cream-200/60 mb-6 text-sm">The page you're looking for doesn't exist.</p>
      <Link
        to="/"
        className="inline-block px-6 py-3 bg-copper-500 hover:bg-copper-400 text-cream-50 rounded-xl font-semibold transition-colors"
      >
        Go Home
      </Link>
    </div>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <ToastContainer />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />

          {/* Legal Routes */}
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/refund" element={<Refund />} />

          <Route path="/oauth/consent" element={
            <RequireAuth>
              <OAuthConsent />
            </RequireAuth>
          } />

          <Route element={<RequireAuth />}>
            <Route element={<MainLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/catalog" element={<Catalog />} />
              <Route path="/scan" element={<Scanner />} />
              <Route path="/collection" element={<Collection />} />
              <Route path="/binders/:id" element={<BinderDetail />} />
              <Route path="/wishlists/:id" element={<WishlistDetail />} />
              <Route path="/market" element={<Marketplace />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/checkout/success" element={<CheckoutSuccess />} />
            </Route>
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
